import React, { useMemo, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { groupBy, orderBy, times } from "lodash";
import { useOutletContext } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { Flow, FlowVersion, FlowVersionFlowChild } from "src/api/flowTypes";
import { useFlow } from "src/api/queries";
import { FlowVersionStatus } from "src/clients/flow-api/api";
import {
  ArchivedVersionRow,
  ArchivedVersionRowSkeleton,
  ArchivedVersionsTable,
} from "src/flow/ArchivedVersionsTable";
import {
  DraftVersionRow,
  DraftVersionRowSkeleton,
  DraftVersionsTable,
} from "src/flow/DraftVersionsTable";
import { FlowPageContextNoData } from "src/flow/FlowPage";
import { FlowVersionControlModals } from "src/flow/FlowVersionControlModals";
import {
  InReviewVersionRow,
  InReviewVersionRowSkeleton,
  InReviewVersionsTable,
} from "src/flow/InReviewVersionTable";
import {
  PublishedVersionRow,
  PublishedVersionRowSkeleton,
  PublishedVersionsTable,
} from "src/flow/PublishedVersionsTable";
import { SubHeader, SubHeaderFilter } from "src/flow/SubHeader";
import {
  BuildOpenModalActionFn,
  useFlowVersionModals,
} from "src/flow/useFlowVersionModals";
import { usePublishFlowVersionCallback } from "src/flow/usePublishFlowVersionCallback";
import { useCapabilities } from "src/hooks/useCapabilities";
import { tableWidth } from "src/layout/constants";
import {
  FeVersionStatusFilterOptions,
  FeVersionStatusFilterValue,
  versionIsInReview,
} from "src/utils/flowVersion";

const versionUpdatedAtComparator = (version: FlowVersion) =>
  new Date(version.updated_at).valueOf();
const versionPublishedAtComparator = (version: FlowVersion) =>
  new Date(version.meta.published_at!).valueOf();
const versionArchivedAtComparator = (version: FlowVersion) =>
  new Date(version.meta.archived_at!).valueOf();

const versionHasPublishedParentFlows = (version: FlowVersionFlowChild) =>
  version.parent_flows?.some((parentFlow) =>
    parentFlow.versions?.some(
      (version) => version.status === FlowVersionStatus.PUBLISHED,
    ),
  );

const PublishedVersions: React.FC<{
  flow: Flow | undefined;
  versions?: FlowVersionFlowChild[];
  showManageTrafficOption: boolean;
  getOpenModalAction: BuildOpenModalActionFn;
}> = ({ versions, flow, showManageTrafficOption, getOpenModalAction }) => {
  const isLoading = !flow;
  const hasLiveParentFlows = useMemo(
    () =>
      versions
        ? versions.reduce(
            (acc, version) => ({
              ...acc,
              [version.id]: versionHasPublishedParentFlows(version) ?? false,
            }),
            {} as Record<string, boolean>,
          )
        : {},
    [versions],
  );

  if (!isLoading && !versions) return null;

  return (
    <PublishedVersionsTable animate={!isLoading}>
      {isLoading
        ? times(3, (i) => <PublishedVersionRowSkeleton key={i} />)
        : orderBy(versions, versionPublishedAtComparator, "desc").map(
            (version) => {
              const flowTrafficPolicyPercentage =
                flow.active_traffic_policy?.policy?.flow_versions?.find(
                  (v) => v.flow_version_id === version.id,
                )?.percentage;
              const trafficAllocation = flowTrafficPolicyPercentage
                ? `${flowTrafficPolicyPercentage}%`
                : "-";
              return (
                <PublishedVersionRow
                  key={version.id}
                  isDefault={flow.default_version === version.id}
                  isDefaultSandbox={flow.default_sandbox_version === version.id}
                  showManageTrafficOption={showManageTrafficOption}
                  trafficAllocation={trafficAllocation}
                  usingTrafficPolicy={!!flow.active_traffic_policy}
                  version={version}
                  onArchive={
                    hasLiveParentFlows[version.id]
                      ? getOpenModalAction(
                          "archive_version_live_parents",
                          version,
                        )
                      : getOpenModalAction("archive_version", version)
                  }
                  onDuplicate={getOpenModalAction("add_version", version)}
                  onDuplicateToFlow={getOpenModalAction(
                    "duplicate_version_to_flow",
                    version,
                  )}
                  onManageTraffic={getOpenModalAction(
                    "manage_traffic",
                    version,
                  )}
                  onSetDefaultSandbox={getOpenModalAction(
                    "default_sandbox_version",
                    version,
                  )}
                />
              );
            },
          )}
    </PublishedVersionsTable>
  );
};

const InReviewVersions: React.FC<{
  flow: Flow | undefined;
  versions?: FlowVersionFlowChild[];
  getOpenModalAction: BuildOpenModalActionFn;
}> = ({ versions, flow, getOpenModalAction }) => {
  const { flowVersions } = useCapabilities();
  const handlePublish = usePublishFlowVersionCallback({
    flowId: flow?.id,
    buildOpenModalAction: getOpenModalAction,
  });

  const isLoading = !flow;
  if (!isLoading && (!versions || versions?.length === 0)) return null;

  return (
    <InReviewVersionsTable animate={!isLoading}>
      {isLoading
        ? times(3, (i) => <InReviewVersionRowSkeleton key={i} />)
        : orderBy(versions, versionUpdatedAtComparator, "desc").map(
            (version) => (
              <InReviewVersionRow
                key={version.id}
                canDelete={
                  flow.versions.length > 1 && flowVersions.canDeleteDraft
                }
                isDefaultSandbox={flow.default_sandbox_version === version.id}
                version={version}
                onDelete={getOpenModalAction("delete_version", version)}
                onDuplicate={getOpenModalAction("add_version", version)}
                onDuplicateToFlow={getOpenModalAction(
                  "duplicate_version_to_flow",
                  version,
                )}
                onEdit={getOpenModalAction("edit_version", version)}
                onPublish={() => handlePublish(version)}
                onSetDefaultSandbox={getOpenModalAction(
                  "default_sandbox_version",
                  version,
                )}
              />
            ),
          )}
    </InReviewVersionsTable>
  );
};

const DraftVersions: React.FC<{
  flow: Flow | undefined;
  versions?: FlowVersionFlowChild[];
  getOpenModalAction: BuildOpenModalActionFn;
}> = ({ versions, flow, getOpenModalAction }) => {
  const { flowVersions } = useCapabilities();
  const handlePublish = usePublishFlowVersionCallback({
    flowId: flow?.id,
    buildOpenModalAction: getOpenModalAction,
  });

  const isLoading = !flow;

  if (!isLoading && (!versions || versions?.length === 0)) return null;

  return (
    <DraftVersionsTable animate={!isLoading}>
      {isLoading
        ? times(3, (i) => <DraftVersionRowSkeleton key={i} />)
        : orderBy(versions, versionUpdatedAtComparator, "desc").map(
            (version) => (
              <DraftVersionRow
                key={version.id}
                canDelete={
                  flow.versions.length > 1 && flowVersions.canDeleteDraft
                }
                isDefaultSandbox={flow.default_sandbox_version === version.id}
                version={version}
                onDelete={getOpenModalAction("delete_version", version)}
                onDuplicate={getOpenModalAction("add_version", version)}
                onDuplicateToFlow={getOpenModalAction(
                  "duplicate_version_to_flow",
                  version,
                )}
                onEdit={getOpenModalAction("edit_version", version)}
                onPublish={() => handlePublish(version)}
                onRequestReview={getOpenModalAction("review_version", version)}
                onSetDefaultSandbox={getOpenModalAction(
                  "default_sandbox_version",
                  version,
                )}
              />
            ),
          )}
    </DraftVersionsTable>
  );
};

const versionHasUnpublishedChildFlows = (version: FlowVersionFlowChild) =>
  version.child_flows?.some((childFlow) =>
    childFlow.versions?.some(
      (version) => version.status !== FlowVersionStatus.PUBLISHED,
    ),
  );

const ArchivedVersions: React.FC<{
  versions?: FlowVersionFlowChild[];
  flow: Flow;
  getOpenModalAction: BuildOpenModalActionFn;
}> = ({ flow, versions, getOpenModalAction }) => {
  const hasUnpublishedChildFlows = useMemo(
    () =>
      versions
        ? versions.reduce(
            (acc, version) => ({
              ...acc,
              [version.id]: versionHasUnpublishedChildFlows(version) ?? false,
            }),
            {} as Record<string, boolean>,
          )
        : {},
    [versions],
  );

  return (
    <ArchivedVersionsTable>
      {orderBy(versions, versionArchivedAtComparator, "desc").map((version) => (
        <ArchivedVersionRow
          key={version.id}
          flow={flow}
          version={version}
          onDuplicate={getOpenModalAction("add_version", version)}
          onRepublish={
            hasUnpublishedChildFlows[version.id]
              ? getOpenModalAction("republish_unpublished_child_flows", version)
              : getOpenModalAction("republish_version", version)
          }
        />
      ))}
    </ArchivedVersionsTable>
  );
};

const filterOptions = [
  FeVersionStatusFilterOptions.published,
  FeVersionStatusFilterOptions.inReview,
  FeVersionStatusFilterOptions.drafts,
];

const filterOptionsWithArchived = [
  ...filterOptions,
  FeVersionStatusFilterOptions.archived,
];

export const FlowVersionsContent: React.FC = () => {
  const { flow } = useOutletContext<FlowPageContextNoData>();
  const [filter, setFilter] = useState<FeVersionStatusFilterValue | null>(null);

  const { flowVersions } = useCapabilities();

  // Fetch with includeArchivedFlowVersions when the Archived filter is selected
  const isArchivedFilter = filter === FlowVersionStatus.ARCHIVED;
  const { data: flowWithArchived, isLoading: isLoadingArchived } = useFlow(
    flow?.id,
    {
      includeArchivedFlowVersions: true,
      enabled: isArchivedFilter,
    },
  );

  // Use the flow data with archived versions when the Archived filter is selected
  const flowData =
    isArchivedFilter && flowWithArchived ? flowWithArchived : flow;

  const {
    PUBLISHED,
    DRAFT: allDrafts,
    ARCHIVED,
  } = groupBy(flowData?.versions, "status");

  const drafts = allDrafts?.filter((v) => !versionIsInReview(v));
  const inReviews = allDrafts?.filter((v) => versionIsInReview(v));

  const isTableVisible = (table: FeVersionStatusFilterValue) =>
    filter === null || filter === table;

  const {
    currentModal,
    selectedVersion,
    unpublishedTemplates,
    buildOpenModalAction,
    onClose,
    onAfterLeave,
    onOpenManageTrafficModal,
  } = useFlowVersionModals();

  const atLeastTwoVersionsPublished = flow
    ? flow.versions.filter((v) => v.status === FlowVersionStatus.PUBLISHED)
        .length >= 2
    : false;

  const renderArchivedContent = () => {
    if (isLoadingArchived) {
      return (
        <ArchivedVersionsTable>
          {times(3, (i) => (
            <ArchivedVersionRowSkeleton key={i} />
          ))}
        </ArchivedVersionsTable>
      );
    }

    if (flowData) {
      return (
        <ArchivedVersions
          flow={flowData}
          getOpenModalAction={buildOpenModalAction}
          versions={ARCHIVED}
        />
      );
    }

    return null;
  };

  return (
    <>
      <SubHeader title="Versions" paddedParent>
        <SubHeaderFilter
          dataLoc="filter-versions"
          options={filterOptionsWithArchived.map((option) => ({
            key: option.value,
            value: option.label,
          }))}
          selected={filter}
          onChange={setFilter}
        />
        {flowVersions.canCreate && (
          <SubHeader.Button
            dataLoc="create-version-button"
            icon={faPlus}
            tooltip="Create version"
            onClick={buildOpenModalAction("add_version")}
          />
        )}
      </SubHeader>
      <div
        className={twJoin("mx-auto flex w-full flex-col gap-y-5.5", tableWidth)}
      >
        {isTableVisible(FlowVersionStatus.PUBLISHED) && (
          <PublishedVersions
            flow={flow}
            getOpenModalAction={buildOpenModalAction}
            showManageTrafficOption={
              flowVersions.canSetDefault && atLeastTwoVersionsPublished
            }
            versions={PUBLISHED}
          />
        )}
        {isTableVisible("InReview") && (
          <InReviewVersions
            flow={flow}
            getOpenModalAction={buildOpenModalAction}
            versions={inReviews}
          />
        )}
        {isTableVisible("DraftsNoReview") && (
          <DraftVersions
            flow={flow}
            getOpenModalAction={buildOpenModalAction}
            versions={drafts}
          />
        )}
        {filter === FlowVersionStatus.ARCHIVED && renderArchivedContent()}
        {flowData && (
          <FlowVersionControlModals
            afterLeave={onAfterLeave}
            flow={flowData}
            modal={currentModal}
            openManageTrafficModal={onOpenManageTrafficModal}
            setFilter={setFilter}
            unpublishedTemplates={unpublishedTemplates}
            version={selectedVersion}
            onClose={onClose}
          />
        )}
      </div>
    </>
  );
};
